// Slice 36 — platform-admin roster (DESIGN §6.6). Lets a BesideU Admin invite
// other BesideU Admins (replacing the `wrangler d1 execute` bootstrap as the
// routine path) and suspend / reactivate them. A trimmed sibling of
// [AdminCgcMembers.tsx](./AdminCgcMembers.tsx): admins have no `kind`,
// `modesAllowed`, `Placeholder` status, or identity-history — just a flat
// Active / Suspended lifecycle plus the awaiting-first-sign-in pre-enrolment
// state. Inviting dispatches a magic link; the invitee completes TOTP
// enrolment on first sign-in. Suspending preserves the row for audit history.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import type { AdminMemberSummary } from '@besideu/shared/admin';
import { InviteAdminSchema, type InviteAdminRequest } from '@besideu/shared/admin';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Field, TextInput } from '@/components/ui/Field.tsx';

type StatusFilter = 'all' | 'Active' | 'Suspended';

export function AdminAdmins() {
  const queryClient = useQueryClient();
  const admins = useQuery({
    queryKey: ['admin', 'admins'],
    queryFn: () => api.get<{ admins: AdminMemberSummary[] }>('/api/admin/admins'),
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = (searchParams.get('status') ?? 'all') as StatusFilter;
  const queryText = searchParams.get('q') ?? '';
  const [inviting, setInviting] = useState(false);

  function setStatus(next: StatusFilter) {
    const sp = new URLSearchParams(searchParams);
    if (next === 'all') sp.delete('status');
    else sp.set('status', next);
    setSearchParams(sp, { replace: true });
  }
  function setQuery(next: string) {
    const sp = new URLSearchParams(searchParams);
    if (!next) sp.delete('q');
    else sp.set('q', next);
    setSearchParams(sp, { replace: true });
  }

  const filtered = useMemo(() => {
    const all = admins.data?.admins ?? [];
    const q = queryText.trim().toLowerCase();
    return all.filter((m) => {
      if (statusFilter !== 'all' && m.status !== statusFilter) return false;
      if (!q) return true;
      return m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q);
    });
  }, [admins.data, statusFilter, queryText]);

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Platform Admins</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            BesideU operators with cross-tenant dashboard access. Inviting an admin dispatches a
            magic link; they complete TOTP enrolment on first sign-in. Suspending blocks sign-in
            immediately and preserves the row for audit history.
          </p>
        </div>
        <Button
          variant="primary"
          className="shrink-0"
          onClick={() => setInviting((v) => !v)}
          aria-expanded={inviting}
        >
          {inviting ? 'Cancel invite' : 'Invite admin'}
        </Button>
      </header>

      {inviting ? (
        <InviteAdminPanel
          onDone={async () => {
            setInviting(false);
            await queryClient.invalidateQueries({ queryKey: ['admin', 'admins'] });
            await queryClient.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
          }}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1 rounded-md border border-border bg-background p-1">
          {(['all', 'Active', 'Suspended'] as const).map((s) => (
            <FilterChip
              key={s}
              active={statusFilter === s}
              onClick={() => setStatus(s)}
              label={s === 'all' ? 'All' : s}
              count={
                s === 'all'
                  ? admins.data?.admins.length
                  : admins.data?.admins.filter((m) => m.status === s).length
              }
            />
          ))}
        </div>
        <input
          type="search"
          value={queryText}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name or email"
          className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none sm:max-w-xs"
        />
      </div>

      {admins.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : admins.error ? (
        <p className="text-sm text-traffic-red">Could not load platform admins.</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
          {admins.data.admins.length === 0
            ? 'No platform admins yet.'
            : 'No admins match the current filters.'}
        </div>
      ) : (
        <AdminsTable admins={filtered} />
      )}
    </section>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number | undefined;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded px-3 py-1.5 text-xs font-medium transition',
        active
          ? 'bg-foreground text-background'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      ].join(' ')}
    >
      {label}
      {typeof count === 'number' ? (
        <span className={`ml-1.5 ${active ? 'opacity-70' : 'opacity-60'}`}>{count}</span>
      ) : null}
    </button>
  );
}

function AdminsTable({ admins }: { admins: AdminMemberSummary[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-muted-foreground">
          <tr>
            <th className="px-4 py-2 font-medium">Name</th>
            <th className="px-4 py-2 font-medium">Email</th>
            <th className="px-4 py-2 font-medium">Status</th>
            <th className="px-4 py-2 font-medium">Last sign-in</th>
            <th className="px-4 py-2 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {admins.map((m) => (
            <AdminRow key={m.id} admin={m} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AdminRow({ admin }: { admin: AdminMemberSummary }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sent'>('idle');

  function invalidateAll() {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'admins'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
  }

  const suspend = useMutation({
    mutationFn: () => api.post(`/api/admin/admins/${admin.id}/suspend`),
    onSuccess: () => {
      setError(null);
      invalidateAll();
    },
    onError: (e) => {
      setError(e instanceof ApiError ? translateSuspendError(e) : 'Suspend failed.');
    },
  });

  const reactivate = useMutation({
    mutationFn: () => api.post(`/api/admin/admins/${admin.id}/reactivate`),
    onSuccess: () => {
      setError(null);
      invalidateAll();
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.message : 'Reactivate failed.');
    },
  });

  const resend = useMutation({
    mutationFn: () => api.post(`/api/admin/admins/${admin.id}/resend-invite`),
    onSuccess: () => {
      setError(null);
      setResendStatus('sent');
      window.setTimeout(() => setResendStatus('idle'), 3000);
    },
    onError: (e) => {
      setError(e instanceof ApiError ? translateResendError(e) : 'Resend failed.');
    },
  });

  // Pre-enrolment admins can have their invite re-sent — but not while
  // suspended: the auth flow filters on `status = 'Active'`, so a resent link
  // would be dead-on-arrival (the server refuses with `suspended` too).
  // Reactivate first, then resend. Self-suspend is blocked separately (an admin
  // must not lock themselves out — another admin has to do it); the server
  // enforces that too, the disabled button is just the affordance.
  const canResend = !admin.totpEnrolled && admin.status !== 'Suspended';

  return (
    <>
      <tr className="border-t border-border align-top">
        <td className="px-4 py-2 font-medium">
          {admin.name}
          {admin.isSelf ? (
            <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              You
            </span>
          ) : null}
        </td>
        <td className="px-4 py-2 text-muted-foreground">{admin.email}</td>
        <td className="px-4 py-2">
          <AdminStatusPill admin={admin} />
        </td>
        <td className="px-4 py-2 text-muted-foreground">
          {admin.lastSignInAt ? new Date(admin.lastSignInAt).toLocaleString() : '—'}
        </td>
        <td className="px-4 py-2">
          <div className="flex flex-wrap items-center justify-end gap-1">
            {canResend ? (
              resendStatus === 'sent' ? (
                <span className="px-2 py-1 text-xs text-traffic-green-text" role="status">
                  Sent
                </span>
              ) : (
                <Button
                  variant="ghost"
                  className="px-2 py-1 text-xs"
                  loading={resend.isPending}
                  onClick={() => resend.mutate()}
                  aria-label={`Resend invite to ${admin.name}`}
                >
                  Resend invite
                </Button>
              )
            ) : null}
            {admin.status === 'Active' ? (
              <Button
                variant="ghost"
                className="px-2 py-1 text-xs text-traffic-red disabled:text-muted-foreground"
                disabled={admin.isSelf}
                {...(admin.isSelf
                  ? { title: 'You cannot suspend yourself. Another admin must do it.' }
                  : {})}
                onClick={() => {
                  if (
                    window.confirm(
                      `Suspend ${admin.name}? They will be unable to sign in until reactivated. Their prior actions remain in the audit log.`,
                    )
                  ) {
                    suspend.mutate();
                  }
                }}
                loading={suspend.isPending}
                aria-label={`Suspend admin ${admin.name}`}
              >
                Suspend
              </Button>
            ) : (
              <Button
                variant="ghost"
                className="px-2 py-1 text-xs"
                onClick={() => {
                  if (
                    window.confirm(`Reactivate ${admin.name}? They will be able to sign in again.`)
                  ) {
                    reactivate.mutate();
                  }
                }}
                loading={reactivate.isPending}
                aria-label={`Reactivate admin ${admin.name}`}
              >
                Reactivate
              </Button>
            )}
          </div>
        </td>
      </tr>
      {error && (
        <tr>
          <td
            colSpan={5}
            className="border-t border-border bg-traffic-red/5 px-4 py-2 text-xs text-traffic-red"
          >
            {error}
          </td>
        </tr>
      )}
      {admin.status === 'Suspended' && admin.suspendedAt && (
        <tr>
          <td
            colSpan={5}
            className="border-t border-border bg-muted/20 px-4 py-2 text-xs text-muted-foreground"
          >
            Suspended {new Date(admin.suspendedAt).toLocaleString()}
            {admin.suspendedByAdminName ? ` by ${admin.suspendedByAdminName}` : ''}.
          </td>
        </tr>
      )}
    </>
  );
}

function AdminStatusPill({ admin }: { admin: AdminMemberSummary }) {
  // A green "Active" pill is reserved for admins who have completed TOTP — i.e.
  // who can actually sign in. An invited-but-never-enrolled admin renders as
  // amber "Awaiting first sign-in" so the roster shows who hasn't onboarded.
  const { label, className } =
    admin.status === 'Suspended'
      ? { label: 'Suspended', className: 'bg-traffic-red/10 text-traffic-red' }
      : admin.totpEnrolled
        ? { label: 'Active', className: 'bg-traffic-green/10 text-traffic-green-text' }
        : {
            label: 'Awaiting first sign-in',
            className: 'bg-traffic-amber/20 text-traffic-amber-text',
          };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Invite panel — shown at the top of the page when "Invite admin" is open.
// ---------------------------------------------------------------------------

function InviteAdminPanel({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: (input: InviteAdminRequest) =>
      api.post<{ member: AdminMemberSummary }>('/api/admin/admins', input),
    onSuccess: () => {
      onDone();
    },
    onError: (e) => {
      setError(e instanceof ApiError ? translateInviteError(e) : 'Invite failed.');
    },
  });

  function submit() {
    setError(null);
    const parsed = InviteAdminSchema.safeParse({ name, email });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setError(issue ? humaniseInviteIssue(issue) : 'Invalid input.');
      return;
    }
    invite.mutate(parsed.data);
  }

  return (
    <div className="rounded-lg border border-border bg-muted/10 p-4">
      <h2 className="text-base font-semibold">Invite platform admin</h2>
      <p className="mt-1 mb-4 text-xs text-muted-foreground">
        A magic-link email is sent on submit. The new admin completes TOTP enrolment on first
        sign-in.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name">
          {(id, desc) => (
            <TextInput
              id={id}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              {...desc}
            />
          )}
        </Field>
        <Field label="Email">
          {(id, desc) => (
            <TextInput
              id={id}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
              {...desc}
            />
          )}
        </Field>
      </div>
      {error ? (
        <p className="mt-3 text-sm text-traffic-red" role="alert">
          {error}
        </p>
      ) : null}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" onClick={submit} loading={invite.isPending}>
          Send invite
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error translators — keep API error codes out of the UI.
// ---------------------------------------------------------------------------

function humaniseInviteIssue(issue: { path: PropertyKey[]; message: string }): string {
  const field = issue.path[0];
  if (field === 'name') return 'Please enter a name.';
  if (field === 'email') return 'Please enter a valid email address.';
  return issue.message;
}

function translateInviteError(e: ApiError): string {
  switch (e.message) {
    case 'email_already_exists':
      return 'An admin with that email already exists.';
    case 'invalid_body':
      return 'Please fill in name and email.';
    default:
      return e.message || 'Invite failed.';
  }
}

function translateResendError(e: ApiError): string {
  switch (e.message) {
    case 'already_enrolled':
      return 'Admin has already completed TOTP enrolment.';
    case 'suspended':
      return 'Admin is suspended. Reactivate them before resending an invite.';
    case 'not_found':
      return 'Admin no longer exists.';
    default:
      return e.message || 'Resend failed.';
  }
}

function translateSuspendError(e: ApiError): string {
  switch (e.message) {
    case 'cannot_suspend_self':
      return 'You cannot suspend yourself. Another admin must do it.';
    case 'last_active_admin':
      return 'Cannot suspend the only active admin — invite another first.';
    case 'already_suspended':
      return 'Admin is already suspended.';
    case 'not_found':
      return 'Admin no longer exists.';
    default:
      return e.message || 'Suspend failed.';
  }
}
